/* eslint-disable @typescript-eslint/no-empty-interface */
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { Autocomplete, AutocompleteRenderInputParams, InputAdornment, ListSubheader, ListSubheaderProps, MenuItem, SelectChangeEvent } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import NumberFormatter from "@shared/mirage/helpers/NumberFormatter";
import { ContentType } from "@shared/mirage/metadata/Contracts";
import dayjs, { Dayjs } from "dayjs";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ModeTypes } from "../mirageGlobalButtons/MirageRecordActionButtons";
import "./Field.css";
import LookupField from "./LookupField";
import MirageFormControl from "./MirageFormControl";
import { useDrop } from "react-dnd";
import { DigitalClock } from "@mui/x-date-pickers";
import datePickerValueFormatter from "../helpers/datePickerValueFormatter";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { IEntityPageResult } from "@shared/mirage/core/contracts/IEntityPageResult";
import { Filter } from "@shared/mirage/entityTable/Filter";
import { TextNavigate } from "../buttons/TextNavigate/TextNavigate";
import { MirageConstants } from "@shared/mirage/mirageConstants";
import "dayjs/locale/en-gb";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { DateRange } from "@mui/x-date-pickers-pro";
import { StringsHelper } from "@shared/mirage/helpers/StringsHelper";
import { NamedOption } from "@shared/mirage/core/contracts/NamedOption";
import { PicklistItem } from "@shared/mirage/PickLists/PicklistFile";
import { usePicklist } from "@mirage/store/PicklistContext";

/**
 * It is a multifunctional Field component that uses smaller field components to show appropriate field type
 * @param numberFormat only for numeric fields.
 * @param startAdornment numberFormat is declared - you can't override default adornment.
 * @param endAdornment numberFormat is declared - you can't override default adornment.
 * @param {string[]} options only for picklist - it is loaded from binding, but you can override with custom list.
 * @param  relatedObject only for lookups - it is loaded from binding, otherwise you can provide name of the table e.g.: BC_APPLICATION, USERS_...
 * @returns Complete field found by contentType - which is provided by ClientBinding of chosen entity.
 */
const Field: React.FC<FieldProps> = memo((props) => {
    if (props.isLoading) {
        return <Skeleton variant="text" sx={{ maxWidth: "300px", marginBottom: "1rem" }} />;
    }
    switch (props.contentType) {
        case "LOOKUP":
            return <LookupField {...props} />;
        case "CHECKBOX":
            return <MirageCheckbox {...props} />;
        case "DATE":
            return <MirageDatePicker {...props} />;
        case "DATETIME":
            return <MirageDateTimePicker {...props} />;
        case "TIME":
            return <MirageTimePicker {...props} amPm={true} />;
        case "EMAIL":
            return <MirageTextField {...props} isEmail />;
        case "ID":
            throw Error(`Content Type is no longer supported. Provided type - ${props.contentType} and label - ${props.label}`);
        case "NUMBER":
            return <MirageNumberField {...props} />;
        case "PHONE":
            return <MirageTextField {...props} isPhone />;
        case "PICKLIST":
            return <MiragePicklist {...props} />;
        case "TEXT": {
            return <MirageTextField {...props} />;
        }
        case "TEXTAREA":
            return <MirageTextField {...props} isTextArea />;
        case "AUTOCOMPLETE":
            return <MirageAutocomplete {...props} />;
        case "DATEBETWEEN":
            return <MirageDateBetween {...props} />;
        default:
            throw Error(`No content type found for the field. Provided type - ${props.contentType} and label - ${props.label}`);
    }
});

const MirageTextField: React.FC<MirageTextFieldProps> = (props) => {
    const {
        label,
        value,
        mode,
        readOnly,
        onChange,
        className,
        isTextArea,
        isPhone,
        helpText,
        errorMsg,
        isRequired,
        containerClassName,
        labelClassName,
        errorMsgClassName,
        acceptDropFrom: dropFor,
        onDrop,
        beforeLabelAdornment,
        viewClassName,
        fullWidth,
        placeholder,
        navigateTo,
        contentType,
        maxLength,
        afterInputEndAdornment,
        hideOnEmpty,
        fieldName,
        rows,
    } = props;
    const fieldClassName = `mirageFormControl__field ${fullWidth ? "mirageFormControl__field--fullWidth" : ""}`.trim();
    const [{ canDrop, isOver }, drop] = useDrop(() => ({
        accept: (dropFor as string) || "___",
        drop: (item: { text: string }) => {
            if ("text" in item && typeof onDrop === "function") {
                onDrop(item.text);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
            droppedItem: monitor.getItem(),
        }),
    }));
    const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const stripped = StringsHelper.stripCorruptChars(e.target.value);
        onChange && onChange(e, stripped);
    };
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const onBlur = (e: MirageOnChangeEvent) => {
        if (!isTextArea && onChange && value) {
            if (value !== trimmedValue) {
                onChange(e, trimmedValue);
            }
        }
    };

    if (hideOnEmpty && !trimmedValue) {
        return null;
    }

    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label={label}
            mode={mode}
            errorMsgClassName={errorMsgClassName}
            readOnly={readOnly}
            helpText={helpText}
            isRequired={isRequired}
            errorMsg={errorMsg}
            beforeLabelAdornment={beforeLabelAdornment}
            contentType={contentType}
        >
            {getField()}
            {afterInputEndAdornment ? afterInputEndAdornment : null}
        </MirageFormControl>
    );

    function getField() {
        if (mode === "view" || navigateTo) {
            const text = navigateTo ? (
                <TextNavigate disabled={readOnly} navigateTo={navigateTo}>
                    {value as string}
                </TextNavigate>
            ) : (
                (value as string)
            );
            return <span className={"mirageFormControl__field--view " + (viewClassName || "")}>{text}</span>;
        }
        return (
            <TextField
                ref={drop}
                inputProps={{ inputMode: isPhone ? "tel" : "text", maxLength }}
                InputProps={{
                    startAdornment: props.startAdornment,
                    endAdornment: props.endAdornment,
                }}
                value={(value as string) || ""}
                className={`${fieldClassName} ${className ? className : ""}`.trim()}
                unselectable={readOnly ? "on" : "off"}
                disabled={readOnly}
                type="text"
                size="small"
                error={!!errorMsg || (isRequired && !value)}
                onChange={handleOnChange}
                multiline={isTextArea}
                minRows={isTextArea ? rows || 3 : ""}
                placeholder={placeholder}
                onBlur={onBlur}
                name={fieldName}
            />
        );
    }
};

const MirageNumberField: React.FC<FieldProps> = (props) => {
    const {
        label,
        mode,
        readOnly,
        className,
        onChange,
        numberFormat,
        helpText,
        errorMsg,
        isRequired,
        containerClassName,
        labelClassName,
        minValue,
        maxValue,
        roundTo,
        viewClassName,
        afterInputEndAdornment,
    } = props;
    const [value, setValue] = useState(checkIfValueIsCorrectNumber(props.value) ? props.value : "");
    const defaultDecimalLimit = 2;
    const defaultInputStep = 1;

    useEffect(() => {
        if (!checkIfValueIsCorrectNumber(props.value)) {
            setValue("");
            return;
        }
        const roundedValue = limitDecimalCharacters(props.value as number, roundTo);
        setValue(roundedValue as number);
    }, [props.value, roundTo]);

    const fieldClassName = "mirageFormControl__field";
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = checkIfValueIsCorrectNumber(e.target.valueAsNumber) ? e.target.valueAsNumber : "";
        setValue(newValue);
        if (onChange) {
            onChange(e, e.target.valueAsNumber || null);
        }
    };
    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (checkIfValueIsCorrectNumber(e.target.valueAsNumber)) {
            const roundValue = typeof roundTo === "number" ? roundTo : defaultDecimalLimit;
            const roundedValue = Number(e.target.valueAsNumber.toFixed(roundValue));
            setValue(roundedValue);
            if (onChange) {
                onChange(e, roundedValue);
            }
        }
    };
    const fieldHasError = Boolean(errorMsg);
    const valueIsNumeric = typeof value === "number" && !Number.isNaN(value);
    const fieldIsEmptyAndRequired = Boolean(isRequired && !valueIsNumeric);
    const valueIsGreaterThanMaxAcceptable = Boolean(typeof maxValue === "number" && valueIsNumeric && value > maxValue);
    const valueIsLessThanMinimumAcceptable = Boolean(typeof minValue === "number" && valueIsNumeric && value < minValue);
    const minValueDecimal = minValue?.toString().split(".")[1]?.length || 0;
    const maxValueDecimal = maxValue?.toString().split(".")[1]?.length || 0;
    const inputStep = getInputStep();
    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label={label}
            mode={mode}
            readOnly={readOnly}
            helpText={helpText}
            isRequired={isRequired}
            errorMsg={errorMsg}
        >
            {getField()}
            {afterInputEndAdornment ? afterInputEndAdornment : null}
        </MirageFormControl>
    );

    function getField() {
        if (mode === "view") {
            const modifier = "mirageFormControl__field--" + numberFormat + (viewClassName || "");
            return <span className={"mirageFormControl__field--view " + modifier}>{formatViewValue()}</span>;
        }
        return (
            <TextField
                InputProps={{
                    startAdornment: getStartAdornment(),
                    endAdornment: getEndAdornment(),
                    inputProps: {
                        step: inputStep,
                        min: minValue,
                        max: maxValue ? maxValue : null,
                    },
                    sx: {
                        "& input": {
                            textAlign: numberFormat === "gbp" ? "right" : "left",
                        },
                    },
                }}
                value={value}
                className={`${fieldClassName} ${className ? className : ""}`.trim()}
                unselectable={readOnly ? "on" : "off"}
                disabled={readOnly}
                type="number"
                size="small"
                onChange={handleOnChange}
                error={fieldHasError || fieldIsEmptyAndRequired || valueIsGreaterThanMaxAcceptable || valueIsLessThanMinimumAcceptable}
                onBlur={handleOnBlur}
            />
        );
    }

    function getInputStep() {
        if (!roundTo && !minValueDecimal && !maxValueDecimal) {
            return defaultInputStep;
        }
        if (roundTo === 0) {
            return 1;
        } else if (roundTo === 1) {
            return 0.1;
        } else if (roundTo === 2) {
            return 0.01;
        } else if (minValueDecimal === 1 || maxValueDecimal === 1) {
            return 0.1;
        } else if (minValueDecimal === 2 || maxValueDecimal === 2) {
            return 0.01;
        } else {
            return defaultDecimalLimit;
        }
    }

    function checkIfValueIsCorrectNumber(value: any) {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return false;
        }
        return true;
    }

    function formatViewValue(): React.ReactNode {
        if (value === null || Number.isNaN(value)) {
            return "";
        }
        switch (numberFormat) {
            case "gbp": {
                return NumberFormatter.formatToGBP(value);
            }
            case "percent": {
                return NumberFormatter.formatPercent(value);
            }
            case "squareMeters": {
                return NumberFormatter.formatSquareMeters(value);
            }
            case "meters": {
                return NumberFormatter.formatMeters(value);
            }
            case "minutes": {
                return NumberFormatter.formatMinutes(value);
            }
            case "acres": {
                return NumberFormatter.formatAcres(value);
            }
            case "hectare": {
                return NumberFormatter.formatHectares(value);
            }
            case "squareKm": {
                return (
                    <>
                        {value || 0} km<sup>2</sup>
                    </>
                );
            }
            default: {
                return (
                    <>
                        {value || 0} {numberFormat}
                    </>
                );
            }
        }
    }

    function getStartAdornment(): JSX.Element | null {
        if (props.startAdornment && !numberFormat) {
            return props.startAdornment;
        }
        switch (numberFormat) {
            case "gbp": {
                return <InputAdornment position="start">{MirageTheme.symbols.pound}</InputAdornment>;
            }
        }
        return null;
    }
    function getEndAdornment(): JSX.Element | null {
        if (props.endAdornment && !numberFormat) {
            return props.endAdornment;
        }
        switch (numberFormat) {
            case "gbp": {
                return null;
            }
            case "percent": {
                return <InputAdornment position="end">{MirageTheme.symbols.percent}</InputAdornment>;
            }
            case "squareMeters": {
                return <InputAdornment position="end">{MirageTheme.symbols.squareMeters}</InputAdornment>;
            }
            case "minutes": {
                return <InputAdornment position="end">min.</InputAdornment>;
            }
            case "meters": {
                return <InputAdornment position="end">m</InputAdornment>;
            }
            case "acres": {
                return <InputAdornment position="end">acre</InputAdornment>;
            }
            case "hectare": {
                return <InputAdornment position="end">ha</InputAdornment>;
            }
            case "squareKm": {
                return (
                    <InputAdornment position="end">
                        <span>
                            km<sup>2</sup>
                        </span>
                    </InputAdornment>
                );
            }
        }
        return <InputAdornment position="end">{numberFormat}</InputAdornment>;
    }
    function limitDecimalCharacters(value: number, decimalLimit: number | null | undefined) {
        const numberOfDecimals = decimalLimit !== null && !Number.isNaN(decimalLimit) ? decimalLimit : defaultDecimalLimit;
        // Convert the number to a string
        const numberString = value.toString();

        // Find the index of the decimal point
        const decimalIndex = numberString.indexOf(".");
        const hasENotation = numberString.includes("e");

        // If the decimal point is not found or the decimal limit is 0, return the integer part of the number
        if (decimalIndex === -1 || numberOfDecimals === 0 || hasENotation) {
            return Math.floor(value);
        }

        // Get the decimal portion of the number
        const decimalPart = numberString.substring(decimalIndex + 1);

        // Truncate the decimal part to the specified limit
        const truncatedDecimal = decimalPart.substring(0, numberOfDecimals);

        // Construct the final number with truncated decimal part
        const finalNumber = parseFloat(numberString.substring(0, decimalIndex + 1) + truncatedDecimal);

        return finalNumber;
    }
};

const MiragePicklist: React.FC<MiragePicklistProps> = (props) => {
    const {
        label,
        mode,
        readOnly,
        className,
        viewClassName,
        onChange,
        options: propOptions,
        helpText,
        isRequired,
        errorMsg,
        value: propValue,
        containerClassName,
        labelClassName,
        picklistName,
    } = props;

    const picklistContext = usePicklist();
    const fieldClassName = "mirageFormControl__field";
    const EMPTY_OPTION_LABEL = "(none)";

    const contextItems = picklistName ? picklistContext.getPicklistItems(picklistName) : null;
    const contextOptions = contextItems && contextItems.length > 0 ? getFilteredPicklistItems(contextItems) : [];

    const options = picklistName && contextOptions.length > 0 ? contextOptions : propOptions;
    const areOptionsNamed = checkAreOptionsNamed(options);
    const optionsProvided = !!options?.length;
    const value = getSelectValue();

    const NO_OPTIONS_FOUND_ELEMENT = (
        <MenuItem disabled value="No options found" key={`no-options-picklist-${label}`}>
            No options found
        </MenuItem>
    );

    function getFilteredPicklistItems(items: PicklistItem[]): string[] {
        return items.map((item) => item.label);
    }
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (typeof onChange === "function") {
            onChange(event, event.target.value);
        }
    };
    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label={label}
            mode={mode}
            readOnly={readOnly}
            helpText={helpText}
            isRequired={isRequired}
            errorMsg={errorMsg}
        >
            {getPicklistField()}
        </MirageFormControl>
    );

    function getPicklistField() {
        if (mode === "view") {
            return <span className={"mirageFormControl__field--view " + (viewClassName || "")}>{renderValue(value)}</span>;
        }
        return (
            <TextField
                select
                onChange={handleChange}
                size="small"
                disabled={readOnly}
                className={`${fieldClassName} ${className ? className : ""}`.trim()}
                value={value}
                error={!!errorMsg || (isRequired && !propValue)}
                slotProps={{
                    select: {
                        renderValue,
                        displayEmpty: true,
                    },
                }}
            >
                {getOptions()}
            </TextField>
        );
    }

    function renderValue(valueToRender: unknown) {
        if (typeof valueToRender === "string") {
            return valueToRender;
        } else if (areOptionsNamed) {
            return (valueToRender as NamedOption).label;
        }
        return "";
    }

    function getSelectValue(): string | NamedOption {
        const namedRecordValue = areOptionsNamed && options?.filter(({ value: optionValue }) => propValue === optionValue)[0];
        if (namedRecordValue) {
            return namedRecordValue;
        }
        return (propValue || "") as string;
    }

    function getOptions() {
        if (!optionsProvided) {
            return NO_OPTIONS_FOUND_ELEMENT;
        }
        ensureEmptyOptionAvailable();
        if (areOptionsNamed) {
            return options?.map(({ label, value }) => getOptionItems(label, value));
        }
        return options?.map((option) => getOptionItems(option, option));
    }

    function ensureEmptyOptionAvailable() {
        if (!isRequired) {
            if (!areOptionsNamed) {
                if (!options?.includes(EMPTY_OPTION_LABEL)) {
                    options?.unshift(EMPTY_OPTION_LABEL);
                }
            } else {
                if (!options?.find(({ label }) => label === EMPTY_OPTION_LABEL)) {
                    options?.unshift({ label: EMPTY_OPTION_LABEL, value: "" });
                }
            }
        }
    }

    function getOptionItems(label: string, optionValue: string) {
        const isGroupOption = label?.startsWith("*");
        const nextKey = (prefix: string) => Math.random().toString(36).substring(2, 9);
        if (label === EMPTY_OPTION_LABEL) {
            return getMenuItem(label, "");
        } else if (isGroupOption) {
            return (
                <GroupMenuItem muiSkipListHighlight sx={{ lineHeight: "32px", fontWeight: "900" }} key={optionValue}>
                    {label.substring(1)}
                </GroupMenuItem>
            );
        }
        return getMenuItem(label, optionValue, nextKey(optionValue));
    }

    function getMenuItem(label: string, value: any, customKey?: string) {
        return (
            <MenuItem value={value} key={customKey || value}>
                {label}
            </MenuItem>
        );
    }
};

const MirageTimePicker: React.FC<MirageTimePickerProps> = (props) => {
    const {
        label,
        value,
        onChange,
        viewClassName,
        readOnly,
        amPm,
        mode,
        helpText,
        className,
        errorMsg,
        isRequired,
        containerClassName,
        labelClassName,
        timeFrame,
        minTime,
        maxTime,
    } = props;
    const fieldClassName = "mirageFormControl__field";
    const selectValue = value ? formatAMPM(value as Date) : "";
    const innerValue = value ? dayjs(value as Date) : null;
    const min = getMinTime();
    const max = getMaxTime();
    const [open, setOpen] = useState(false);
    const handleChange = (val: Dayjs | null) => {
        if (val) {
            const date = dayjs(new Date()).set("hour", val?.hour()).set("minute", val?.minute()).set("second", 0).set("millisecond", 0);
            if (date.isValid()) {
                const castedValue = date?.toDate() || null;
                if (onChange) {
                    onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, castedValue);
                }
                setOpen(false);
            }
        }
    };

    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label={label}
            mode={mode}
            readOnly={readOnly}
            helpText={helpText}
            isRequired={isRequired}
            errorMsg={errorMsg}
        >
            {getTimeField()}
        </MirageFormControl>
    );

    function formatAMPM(date: Date) {
        let hours = date.getHours();
        let minutes: any = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        minutes = minutes < 10 ? "0" + minutes : minutes;
        const strTime = hours + ":" + minutes + " " + ampm;
        return strTime;
    }

    function getMinTime() {
        if (minTime) {
            return dayjs(minTime);
        } else if (timeFrame === "pm") {
            return dayjs().set("hour", 12).set("minute", 0).set("second", 0).set("millisecond", 0);
        } else {
            return MirageConstants.WorkingHoursDayRange.start;
        }
    }

    function getMaxTime() {
        if (maxTime) {
            return dayjs(maxTime);
        } else if (timeFrame === "am") {
            return dayjs().set("hour", 11).set("minute", 45).set("second", 0).set("millisecond", 0);
        } else {
            return MirageConstants.WorkingHoursDayRange.end;
        }
    }

    function getTimeField() {
        if (mode === "view") {
            return <span className={"mirageFormControl__field--view " + (viewClassName || "")}>{getFormattedDate(value)}</span>;
        }
        return (
            <TextField
                select
                SelectProps={{
                    renderValue: (value: any) => value,
                    displayEmpty: true,
                    open: open,
                    onOpen: () => setOpen(true),
                    onClose: () => setOpen(false),
                }}
                value={selectValue}
                size="small"
                className={`${fieldClassName} ${className ? className : ""}`.trim()}
                error={!!errorMsg || (isRequired && !value)}
                disabled={readOnly}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                    <DigitalClock disabled={readOnly} ampm={amPm} timeStep={15} value={innerValue} skipDisabled minTime={min} maxTime={max} onChange={handleChange} />
                </LocalizationProvider>
            </TextField>
        );

        function getFormattedDate(value: unknown): string {
            return DateFormatter.formatTimeAmPm(value?.toString());
        }
    }
};

const MirageDateTimePicker: React.FC<MirageDateTimePickerProps> = (props) => {
    const { label, value, viewClassName, onChange, readOnly, mode, helpText, className, errorMsg, isRequired, containerClassName, labelClassName } = props;
    const innerValue = typeof value !== "undefined" ? dayjs(value as Date) : null;
    const datePickerClassName = "mirageFormControl__field--date";

    const handleChange = (val: Dayjs | null) => {
        const castedValue = val?.toDate() || null;
        if (onChange) {
            onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, castedValue);
        }
    };

    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label={label}
            mode={mode}
            readOnly={readOnly}
            helpText={helpText}
            isRequired={isRequired}
            errorMsg={errorMsg}
        >
            {getDateTimeField()}
        </MirageFormControl>
    );

    function getDateTimeField() {
        if (mode === "view") {
            return <span className={"mirageFormControl__field--view " + (viewClassName || "")}>{getFormattedDate(value)}</span>;
        }
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DateTimePicker
                    readOnly={readOnly ? readOnly : false}
                    value={innerValue}
                    onChange={handleChange}
                    className={`${datePickerClassName} ${className ? className : ""}`.trim()}
                    slotProps={{ textField: { size: "small", error: !!errorMsg || (isRequired === true && !value) } }}
                    disabled={readOnly}
                />
            </LocalizationProvider>
        );

        function getFormattedDate(value: unknown): string {
            return DateFormatter.formatToDateTimeAmPm(value?.toString());
        }
    }
};

const MirageDatePicker: React.FC<MirageDatePickerProps> = (props) => {
    const { label, value, viewClassName, onChange, readOnly, mode, helpText, className, errorMsg, isRequired, containerClassName, labelClassName, hideOnEmpty, maxDate } = props;
    const innerValue = datePickerValueFormatter(value);
    const datePickerClassName = "mirageFormControl__field--date";
    const handleChange = (val: Dayjs | null) => {
        const castedValue = val?.set("hour", MirageConstants.DefaultDateFieldHour).set("minute", 0).set("second", 0).set("millisecond", 0).toDate() || null;
        if (onChange) {
            onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, castedValue);
        }
    };

    if (hideOnEmpty && !value) {
        return null;
    }

    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label={label}
            mode={mode}
            helpText={helpText}
            isRequired={isRequired}
            readOnly={readOnly}
            errorMsg={errorMsg}
        >
            {getDateField()}
        </MirageFormControl>
    );

    function getDateField() {
        if (mode === "view") {
            return <span className={"mirageFormControl__field--view " + (viewClassName || "")}>{getFormattedDate(value)}</span>;
        }
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                    value={innerValue}
                    onChange={handleChange}
                    views={["year", "month", "day"]}
                    className={`${datePickerClassName} ${className ? className : ""}`.trim()}
                    slotProps={{
                        field: { clearable: true, onClear: () => handleChange(null) },
                        textField: {
                            size: "small",
                            error: !!errorMsg || (isRequired === true && !value),
                            value: innerValue,
                        },
                    }}
                    maxDate={maxDate}
                    disabled={readOnly}
                />
            </LocalizationProvider>
        );

        function getFormattedDate(value: unknown): string {
            return DateFormatter.formatToDate(value?.toString());
        }
    }
};

const MirageCheckbox: React.FC<MirageCheckboxProps> = (props) => {
    const { label, value, onChange, readOnly, mode, helpText, className, isRequired, containerClassName, labelClassName, endWatermark, indeterminate, contentType } = props;

    const handleOnChange = (e: SelectChangeEvent, checked: boolean) => {
        const castedVal = checked ? 1 : 0;
        if (onChange) {
            onChange(e, castedVal);
        }
    };

    return (
        <MirageFormControl
            label={label}
            labelClassName={labelClassName}
            mode={mode}
            readOnly={readOnly}
            helpText={helpText}
            endWatermark={endWatermark}
            isRequired={isRequired}
            className={`mirageFormControl__field--checkboxView ${containerClassName}`}
            contentType={contentType}
        >
            <Checkbox
                className={`${className ? className : ""}`.trim()}
                disableRipple
                value={label}
                checked={!!value}
                onChange={handleOnChange}
                disabled={mode === "view" || readOnly}
                indeterminate={indeterminate}
            />
        </MirageFormControl>
    );
};

const MirageAutocomplete: React.FC<FieldProps> = ({
    label,
    value,
    className,
    mode,
    readOnly,
    helpText,
    isRequired,
    containerClassName,
    options,
    onChange,
    customPageRequest,
    labelClassName,
    groupBy,
    acceptCustomValues,
}) => {
    const fieldClassName = "mirageFormControl__field";
    const isNamedOption = (option: string | NamedOption | null): option is NamedOption => !!option && typeof option !== "string";
    const areOptionsNamed = checkAreOptionsNamed(options);
    const selectedNamedValue = useMemo(() => {
        const namedRecordValue = areOptionsNamed && options?.filter(({ value: optionValue }) => value === optionValue)[0];
        if (namedRecordValue) {
            return namedRecordValue;
        }
        return (value || "") as string;
    }, [areOptionsNamed, options, value]);
    const handleOnChange = useCallback(
        (e: React.SyntheticEvent<Element, Event>, value: string | NamedOption | null) => {
            if (onChange) {
                const castedValue = isNamedOption(value) ? value.value : value;
                onChange(e as MirageOnChangeEvent, castedValue);
            }
        },
        [onChange]
    );
    const viewValue = useMemo(() => (isNamedOption(selectedNamedValue) ? selectedNamedValue.label : selectedNamedValue), [selectedNamedValue]);
    const autocompleteOptions = useMemo(() => options || [], [options]) as readonly string[];
    const readonlyInput = useCallback((params: AutocompleteRenderInputParams) => <TextField label={""} {...params} />, []);
    const getOptionLabel = useCallback((option: string | NamedOption) => (isNamedOption(option) ? option.label : option), []);
    const onKeyUp = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (customPageRequest) {
                const value = (e.target as HTMLInputElement).value;
                const pageRequest: IEntityPageRequest = {
                    filter: {
                        where: value ? { name: { contains: value } } : {},
                        parts: [],
                    },
                    offset: 0,
                    pageSize: 0,
                    select: undefined,
                    order: {},
                };
                customPageRequest(pageRequest);
                handleOnChange(e, value);
            }
        },
        [customPageRequest, handleOnChange]
    );

    // Handle input changes for freeSolo mode
    const handleInputChange = useCallback(
        (e: React.SyntheticEvent, newInputValue: string, reason: string) => {
            if (!acceptCustomValues) {
                return;
            }
            if (reason === "input" && onChange) {
                onChange(e as MirageOnChangeEvent, newInputValue);
            }
        },
        [acceptCustomValues, onChange]
    );

    const autocompleteProps: any = {};
    if (customPageRequest) {
        autocompleteProps.freeSolo = true;
    }

    return (
        <MirageFormControl label={label} mode={mode} readOnly={readOnly} helpText={helpText} isRequired={isRequired} className={containerClassName} labelClassName={labelClassName}>
            {mode === "view" ? (
                <span className={"mirageFormControl__field--view"}>{viewValue}</span>
            ) : (
                <Autocomplete
                    size="small"
                    getOptionKey={(option: string | NamedOption) => (isNamedOption(option) ? `${option.value}-${option.label}` : option)}
                    className={`${fieldClassName} ${className ? className : ""}`.trim()}
                    value={selectedNamedValue as string}
                    options={autocompleteOptions}
                    renderInput={readonlyInput}
                    onChange={handleOnChange}
                    onInputChange={handleInputChange}
                    getOptionLabel={getOptionLabel}
                    disabled={readOnly}
                    onKeyUp={onKeyUp}
                    {...autocompleteProps}
                    groupBy={groupBy}
                    freeSolo={acceptCustomValues ? true : false}
                />
            )}
        </MirageFormControl>
    );
};

const MirageDateBetween = (props: FieldProps) => {
    const { value, onChange, labelClassName, containerClassName, mode, helpText, isRequired, readOnly, errorMsg } = props;
    const formattedValue = useMemo((): [Dayjs | null, Dayjs | null] => {
        if (!Array.isArray(value)) {
            console.error("DateBetween value is not an array");
            return [null, null];
        }
        const [lower, upper] = value;
        const formattedLower = lower ? dayjs(lower) : null;
        const formattedUpper = upper ? dayjs(upper) : null;
        return [formattedLower, formattedUpper];
    }, [value]);
    const [from, to] = formattedValue as [Dayjs | null, Dayjs | null];
    const dateRangeOnChange = (value: DateRange<Dayjs | null>) => {
        const [lower, upper] = value;
        const lowerDate = lower ? dayjs(lower).toDate() : "";
        const upperDate = upper ? dayjs(upper).toDate() : "";
        onChange && onChange({} as MirageOnChangeEvent, [lowerDate, upperDate]);
    };
    return (
        <MirageFormControl
            labelClassName={labelClassName}
            className={containerClassName}
            label=""
            mode={mode}
            helpText={helpText}
            isRequired={isRequired}
            readOnly={readOnly}
            errorMsg={errorMsg}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateRangePicker
                    slotProps={{
                        shortcuts: {
                            items: [
                                { label: "Clear From", getValue: () => [null, to] },
                                { label: "Clear To", getValue: () => [from, null] },
                                { label: "Reset", getValue: () => [null, null] },
                            ],
                        },
                        textField: ({ position }) => ({
                            size: "small",
                            label: position === "start" ? "From" : "To",
                        }),
                    }}
                    value={formattedValue}
                    onChange={dateRangeOnChange}
                />
            </LocalizationProvider>
        </MirageFormControl>
    );
};

function checkAreOptionsNamed(options: any): options is NamedOption[] {
    return Array.isArray(options) && options?.every((opt: any) => typeof opt !== "string");
}

export interface FieldProps {
    label: string;
    mode: ModeTypes;
    contentType: ContentType;
    onChange?: (e: MirageOnChangeEvent, value: any) => void;
    value?: unknown;
    isValid?: () => boolean; // TODO: @Ievgen Please remove prop from ClientBinding
    navigateTo?: string;
    readOnly?: boolean;
    isLoading?: boolean;
    className?: string;
    containerClassName?: string;
    labelClassName?: string;
    errorMsgClassName?: string;
    viewClassName?: string;
    numberFormat?: NumberFormat;
    startAdornment?: JSX.Element;
    endAdornment?: JSX.Element;
    options?: string[] | NamedOption[] | null;
    relatedObject?: string | null;
    helpText?: (() => JSX.Element) | string | null;
    fieldName?: string;
    relatedRecord?: any | null; //Partial<IBaseEntity> | IAttachment | null;
    isRequired?: boolean;
    errorMsg?: string;
    customPageRequest?: (pageRequest: unknown & IEntityPageRequest) => Promise<IEntityPageResult<any>>;
    minValue?: number | null;
    maxValue?: number | null;
    roundTo?: number | null;
    onDrop?: (text: string) => void;
    acceptDropFrom?: string;
    timeFrame?: TimePickerTimeFrame;
    customColumns?: GridColDef[];
    beforeLabelAdornment?: JSX.Element;
    endWatermark?: string;
    fullWidth?: boolean;
    placeholder?: string;
    indeterminate?: boolean;
    asPicklist?: boolean;
    fetchOnOpen?: boolean;
    sortModel?: GridSortModel;
    filter?: Filter;
    forceFetch?: boolean;
    minTime?: Date | null;
    maxTime?: Date | null;
    showAllUsersFromApplication?: boolean;
    maxLength?: number;
    afterInputEndAdornment?: React.ReactNode;
    hideOnEmpty?: boolean;
    naturalSort?: boolean;
    maxDate?: dayjs.Dayjs;
    rows?: number;
    groupBy?: (option: string | NamedOption) => string;
    leaveOriginalValue?: boolean;
    picklistName?: string | null;
    acceptCustomValues?: boolean;
}

interface MirageTextFieldProps extends FieldProps {
    isTextArea?: boolean;
    isEmail?: boolean;
    isPhone?: boolean;
}
interface MiragePicklistProps extends FieldProps {}
interface MirageDateTimePickerProps extends FieldProps {}
interface MirageDatePickerProps extends FieldProps {}
interface MirageCheckboxProps extends FieldProps {}

interface MirageTimePickerProps extends FieldProps {
    amPm?: boolean;
}

export type TimePickerTimeFrame = "am" | "pm";

export type NumberFormat = "gbp" | "percent" | "squareMeters" | "minutes" | "meters" | "squareKm" | "hectare" | "acres";
export interface BindFieldProps extends FieldProps {}
export type MirageOnChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent;
export function GroupMenuItem(props: ListSubheaderProps & { muiSkipListHighlight: boolean }) {
    const { muiSkipListHighlight, ...other } = props;
    return <ListSubheader {...other} />;
}

export default Field;
